/**
 * Image URL utility for handling both local and cloud storage URLs
 *
 * Problem:
 * - Local storage saves relative paths like `/uploads/logos/file.png`
 * - GCS (bucket) storage saves full URLs like `https://storage.googleapis.com/bucket/file.png`
 * - Frontend was always prepending VITE_API_URL, breaking GCS URLs
 *
 * Solution:
 * - Check if URL is already absolute (http, https, or protocol-relative //)
 * - If absolute, return as-is
 * - If relative, prepend VITE_API_URL with double-slash prevention
 */

/**
 * Builds a complete image URL from a path
 * Handles absolute URLs (cloud storage), protocol-relative URLs, and relative paths
 *
 * @param {string} path - The image URL from database (can be relative or absolute)
 * @returns {string} - Full URL ready for use in img src, or empty string if no path provided
 *
 * @example
 * // Local storage
 * getImageUrl('/uploads/logos/logo.png')
 * // Returns: 'http://localhost:5000/uploads/logos/logo.png'
 *
 * @example
 * // GCS storage
 * getImageUrl('https://storage.googleapis.com/bucket/logos/logo.png')
 * // Returns: 'https://storage.googleapis.com/bucket/logos/logo.png' (unchanged)
 *
 * @example
 * // Protocol-relative URL
 * getImageUrl('//cdn.example.com/image.png')
 * // Returns: '//cdn.example.com/image.png' (unchanged)
 */
export const getImageUrl = (path) => {
  // Return empty string for empty/null paths
  if (!path) return '';

  // Check if it's already an absolute URL (http, https, or protocol-relative //)
  // This handles GCS URLs, CDN URLs, and any other external URLs
  if (/^(?:[a-z]+:)?\/\//i.test(path)) {
    return path;
  }

  // Normalize legacy /assets/ paths (from existing DB records) to /api/assets/
  if (path.startsWith('/assets/') && !path.startsWith('/api/assets/')) {
    path = `/api${path}`;
  }

  // Clean up potential double slashes between base URL and path
  const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}${cleanPath}`;
};

/**
 * Alias for getImageUrl for semantic clarity when working with logos
 */
export const getLogoUrl = getImageUrl;

/**
 * Alias for getImageUrl for semantic clarity when working with vehicle pictures
 */
export const getVehiclePictureUrl = getImageUrl;

/**
 * Alias for getImageUrl for semantic clarity when working with boarding info images
 */
export const getBoardingImageUrl = getImageUrl;

export default getImageUrl;
